import {
  DeleteIcon,
  InformationIcon,
  LinkedPageIcon,
  OpenInNewIcon,
  PlusIcon,
} from '@blocksuite/icons/rc';
import {
  IconButton,
  MenuItem,
  MenuSeparator,
  toast,
  useConfirmModal,
} from '@pique/component';
import { usePageHelper } from '@pique/core/components/blocksuite/block-suite-page-list/utils';
import { IsFavoriteIcon } from '@pique/core/components/pure/icons';
import { useAsyncCallback } from '@pique/core/hooks/pique-async-hooks';
import { CompatibleFavoriteItemsAdapter } from '@pique/core/modules/properties';
import { WorkbenchService } from '@pique/core/modules/workbench';
import { posthog } from '@pique/core/posthog';
import { useI18n } from '@pique/i18n';
import {
  DocsService,
  useLiveData,
  useServices,
  WorkspaceService,
} from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import type { NodeOperation } from '../../tree/types';

export const useExplorerDocNodeOperations = (
  docId: string,
  options: {
    openInfoModal: () => void;
    openNodeCollapsed: () => void;
  }
): NodeOperation[] => {
  const t = useI18n();
  const {
    workbenchService,
    workspaceService,
    docsService,
    compatibleFavoriteItemsAdapter,
  } = useServices({
    DocsService,
    WorkbenchService,
    WorkspaceService,
    CompatibleFavoriteItemsAdapter,
  });
  const { openConfirmModal } = useConfirmModal();

  const docRecord = useLiveData(docsService.list.doc$(docId));

  const { createPage } = usePageHelper(
    workspaceService.workspace.docCollection
  );

  const favorite = useLiveData(
    useMemo(() => {
      return compatibleFavoriteItemsAdapter.isFavorite$(docId, 'doc');
    }, [docId, compatibleFavoriteItemsAdapter])
  );

  const handleOpenInfoModal = useCallback(() => {
    options.openInfoModal();
  }, [options]);

  const handleMoveToTrash = useCallback(() => {
    if (!docRecord) {
      return;
    }
    openConfirmModal({
      title: t['com.pique.moveToTrash.title'](),
      description: t['com.pique.moveToTrash.confirmModal.description']({
        title: docRecord.title$.value,
      }),
      confirmText: t['com.pique.moveToTrash.confirmModal.confirm'](),
      cancelText: t['com.pique.moveToTrash.confirmModal.cancel'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      onConfirm() {
        docRecord.moveToTrash();
        posthog.track('doc_deleted', {
          context: 'navigation_panel',
          control: 'button',
        });
        toast(t['com.pique.toastMessage.movedTrash']());
      },
    });
  }, [docRecord, openConfirmModal, t]);

  const handleOpenInNewTab = useCallback(() => {
    workbenchService.workbench.openDoc(docId, {
      at: 'new-tab',
    });
    posthog.track('doc_opened_in_new_tab', {
      context: 'navigation_panel',
      type: 'doc',
    });
  }, [docId, workbenchService]);

  const handleAddLinkedPage = useAsyncCallback(async () => {
    const newDoc = createPage();
    if (!newDoc) return;
    await docsService.addLinkedDoc(docId, newDoc.id);
    posthog.track('doc_created', { context: 'navigation_panel', control: 'linkDoc' });
    workbenchService.workbench.openDoc(newDoc.id);
    options.openNodeCollapsed();
  }, [createPage, docsService, docId, workbenchService.workbench, options]);

  const handleToggleFavoriteDoc = useCallback(() => {
    compatibleFavoriteItemsAdapter.toggle(docId, 'doc');
  }, [docId, compatibleFavoriteItemsAdapter]);

  return useMemo(
    () => [
      {
        index: 0,
        inline: true,
        view: (
          <IconButton
            size="16"
            icon={<PlusIcon />}
            tooltip={t['com.pique.rootAppSidebar.explorer.doc-add-tooltip']()}
            onClick={handleAddLinkedPage}
          />
        ),
      },
      ...(runtimeConfig.enableInfoModal
        ? [
            {
              index: 50,
              view: (
                <MenuItem
                  prefixIcon={<InformationIcon />}
                  onClick={handleOpenInfoModal}
                >
                  {t['com.pique.page-properties.page-info.view']()}
                </MenuItem>
              ),
            },
          ]
        : []),
      {
        index: 99,
        view: (
          <MenuItem
            prefixIcon={<LinkedPageIcon />}
            onClick={handleAddLinkedPage}
          >
            {t['com.pique.page-operation.add-linked-page']()}
          </MenuItem>
        ),
      },
      {
        index: 99,
        view: (
          <MenuItem prefixIcon={<OpenInNewIcon />} onClick={handleOpenInNewTab}>
            {t['com.pique.workbench.tab.page-menu-open']()}
          </MenuItem>
        ),
      },
      {
        index: 199,
        view: (
          <MenuItem
            prefixIcon={<IsFavoriteIcon favorite={favorite} />}
            onClick={handleToggleFavoriteDoc}
          >
            {favorite
              ? t['com.pique.favoritePageOperation.remove']()
              : t['com.pique.favoritePageOperation.add']()}
          </MenuItem>
        ),
      },
      {
        index: 9999,
        view: <MenuSeparator key="menu-separator" />,
      },
      {
        index: 10000,
        view: (
          <MenuItem
            type={'danger'}
            prefixIcon={<DeleteIcon />}
            onClick={handleMoveToTrash}
          >
            {t['com.pique.moveToTrash.title']()}
          </MenuItem>
        ),
      },
    ],
    [
      favorite,
      handleAddLinkedPage,
      handleMoveToTrash,
      handleOpenInNewTab,
      handleOpenInfoModal,
      handleToggleFavoriteDoc,
      t,
    ]
  );
};
